import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { requiresAtFaultPartyInsured } from "../mvaApplicability";
import { buildSteps, caseScore, contactValid, initialAnswers, tcpaText, TURNSTILE_SITE_KEY } from "./caseQuizLogic";
import { useCaseSubmit } from "./useCaseSubmit";
import { QuizProgress, StrengthMeter } from "./StrengthMeter";
import { CaseStepBody } from "./CaseStepBody";
import { Celebration } from "./Celebration";
import { TrustedFormOffer } from "../trustedForm";
import { fieldRulePage, useFieldRules } from "@/lib/useFieldRules";
import { formBadgeHidden } from "@/lib/previewBadge";

const SLIDE = {
  enter: (d) => ({ opacity: 0, x: d * 48 }),
  center: { opacity: 1, x: 0 },
  exit: (d) => ({ opacity: 0, x: d * -48 }),
};
const CTA = "cf-cta inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-[var(--cf-accent)] px-6 text-base font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-40";

function useQuizTracking(session, step, index, previewSurface, isSample) {
  const started = useRef(false);
  useEffect(() => {
    if (index > 0 && !started.current) {
      started.current = true;
      session?.trackEvent("quiz_started", { variant: "case_quiz" });
      if (!previewSurface && !isSample) {
        window.dispatchEvent(new CustomEvent("lf-tracking-emit", { detail: { event: "form_start", context: {} } }));
      }
    }
    if (step.kind === "contact") session?.trackEvent("quiz_contact_reached", { variant: "case_quiz", stepKey: step.key });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);
}

export const CaseQuizForm = ({ slug, vertical, preview, demo, session, firmName }) => {
  const previewSurface = preview || Boolean(demo);
  const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY) && !previewSurface;
  const [answers, setAnswers] = useState(() => initialAnswers(slug));
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [token, setToken] = useState("");
  const turnstileRef = useRef(null);
  const timer = useRef(null);
  const isMva = requiresAtFaultPartyInsured({ caseType: answers.case_type, verticalSlug: vertical.vertical });
  const { req } = useFieldRules({ style: "case_quiz", page: fieldRulePage(vertical), buyer: vertical.buyerCode });
  const steps = useMemo(() => buildSteps(slug, isMva, req), [slug, isMva, req]);
  const step = steps[Math.min(index, steps.length - 1)];
  const { submit, submitting, error, celebrate, previewOnly } = useCaseSubmit({ vertical, previewSurface, demo });
  useQuizTracking(session, step, index, previewSurface, vertical.isSamplePage);
  useEffect(() => {
    // A Turnstile token works once: get a fresh one for the retry.
    if (error) {
      setToken("");
      turnstileRef.current?.reset();
    }
  }, [error]);
  useEffect(() => () => { clearTimeout(timer.current); }, []);
  useEffect(() => {
    vertical.onAnswers?.(answers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.state, answers.case_type, answers.zip, vertical.onAnswers]);

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));
  const go = (n) => {
    if (n > index) session?.trackEvent("quiz_step_completed", { variant: "case_quiz", stepKey: step.key });
    setDir(n > index ? 1 : -1);
    setIndex(Math.max(0, Math.min(n, steps.length - 1)));
  };
  const onPick = (v) => {
    set(step.field, v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => go(index + 1), 260);
  };
  const send = () => submit({ a: answers, slug, isMva, session, turnstileToken: token });

  return (
    <TrustedFormOffer enabled={!previewSurface && !vertical.isSamplePage}>
    <div className="cf-card relative rounded-2xl p-5 sm:p-7" data-testid="case-quiz-container">
      {previewSurface && !formBadgeHidden() && (
        <div className="mb-4 rounded-md bg-amber-300 px-3 py-1.5 text-center text-[10px] font-bold uppercase tracking-[0.16em] text-black" data-testid="case-quiz-preview-banner">
          Preview · no lead will be sent
        </div>
      )}
      {firmName && (
        <div className="mb-3 text-xs text-slate-400" data-testid="case-quiz-firm">
          Free review by <b className="text-white">{firmName}</b>
        </div>
      )}
      <QuizProgress index={index} total={steps.length} onBack={() => go(index - 1)} />
      <div className="mt-4"><StrengthMeter score={caseScore(answers, slug)} /></div>
      <AnimatePresence mode="wait" custom={dir} initial={false}>
        <motion.div
          key={step.key}
          custom={dir}
          variants={SLIDE}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.26 }}
          className="mt-6"
          data-testid={`case-quiz-step-${step.key}`}
        >
          <h2 className="lp-serif text-xl font-bold text-white sm:text-2xl" data-testid="case-quiz-question">{step.title}</h2>
          {step.subtitle && <p className="mb-5 mt-1 text-sm text-slate-400">{step.subtitle}</p>}
          <CaseStepBody
            step={step}
            answers={answers}
            set={set}
            onPick={onPick}
            consentProps={{ tcpa: tcpaText(vertical), firmName, turnstileEnabled, onToken: setToken, turnstileRef }}
          />
        </motion.div>
      </AnimatePresence>
      {error && (
        <div className="mt-4 rounded-lg border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="case-quiz-error">{error}</div>
      )}
      <StepActions
        step={step}
        canContinue={step.kind === "contact" ? contactValid(answers, !step.zipOptional)
          : (step.kind !== "text" || !step.required || Boolean(answers[step.field]?.trim()))}
        canSubmit={answers.consent && !submitting && (!turnstileEnabled || Boolean(token))}
        submitting={submitting}
        onNext={() => go(index + 1)}
        onSubmit={send}
      />
      <AnimatePresence>{celebrate && <Celebration firmName={firmName} previewOnly={previewOnly} />}</AnimatePresence>
    </div>
    </TrustedFormOffer>
  );
};

const StepActions = ({ step, canContinue, canSubmit, submitting, onNext, onSubmit }) => {
  if (step.kind === "cards") return null;
  if (step.kind === "choices") {
    return step.skippable ? (
      <div className="mt-4 flex justify-end">
        <button type="button" onClick={onNext} className="px-3 py-2 text-sm font-semibold text-slate-400 transition-colors hover:text-white" data-testid="case-quiz-skip-button">
          Skip
        </button>
      </div>
    ) : null;
  }
  return (
    <div className="mt-6 flex items-center justify-end gap-3">
      {step.skippable && (
        <button type="button" onClick={onNext} className="px-3 py-2 text-sm font-semibold text-slate-400 transition-colors hover:text-white" data-testid="case-quiz-skip-button">
          Skip
        </button>
      )}
      {step.kind === "consent" ? (
        <button type="button" onClick={onSubmit} disabled={!canSubmit} name="submit" data-tf-element-role="submit" className={`${CTA} w-full sm:w-auto`} data-testid="case-quiz-submit-button">
          {submitting ? "Sending…" : "Send my free case review"} <ArrowRight size={18} weight="bold" />
        </button>
      ) : (
        <button type="button" onClick={onNext} disabled={!canContinue} className={CTA} data-testid="case-quiz-next-button">
          Continue <ArrowRight size={18} weight="bold" />
        </button>
      )}
    </div>
  );
};
